<template>
  <div class="mt-4">
    <v-card class="mx-auto" max-width="97%" elevation="2">
      <v-toolbar dark dense color="#37474F">
        <v-toolbar-title>토큰 컨텐츠 · 토큰 경매</v-toolbar-title>
        <v-spacer></v-spacer>
        <span class="text-caption grey--text">db_cpu · PUBLIC_AUCTION_*</span>
      </v-toolbar>

      <v-tabs v-model="tab" background-color="grey lighten-4" color="deep-orange" show-arrows>
        <v-tab><v-icon left small>mdi-gamepad-variant-outline</v-icon>게임 템플릿</v-tab>
        <v-tab><v-icon left small>mdi-gift-outline</v-icon>보상 관리</v-tab>
        <v-tab><v-icon left small>mdi-monitor-dashboard</v-icon>회차 · 모니터링</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <!-- ============ 1) 게임 템플릿 ============ -->
        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <div class="d-flex align-center mb-3">
                <div class="text-subtitle-1 font-weight-bold">게임 템플릿</div>
                <v-spacer></v-spacer>
                <v-btn small color="deep-orange" dark depressed @click="openTemplateDialog()">
                  <v-icon left small>mdi-plus</v-icon>새 템플릿
                </v-btn>
                <v-btn small text class="ml-1" @click="loadTemplates"><v-icon small>mdi-refresh</v-icon></v-btn>
              </div>

              <v-data-table
                :headers="templateHeaders"
                :items="templates"
                :loading="loadingTemplates"
                :item-class="templateRowClass"
                dense
                :items-per-page="20"
                class="elevation-1"
              >
                <template v-slot:[`item.BANNER_IMG_URL`]="{ item }">
                  <v-avatar tile size="40" v-if="item.BANNER_IMG_URL"><img :src="imgUrl(item.BANNER_IMG_URL)"/></v-avatar>
                  <v-icon v-else small color="grey lighten-1">mdi-image-off-outline</v-icon>
                </template>
                <template v-slot:[`item.ENTRY_TYPE`]="{ item }">
                  <v-chip x-small :color="item.ENTRY_TYPE === 'SINGLE' ? 'purple lighten-4' : 'blue lighten-4'">{{ item.ENTRY_TYPE }}</v-chip>
                </template>
                <template v-slot:[`item.STATE`]="{ item }">
                  <v-switch
                    :input-value="item.STATE === 'ENABLE'"
                    :label="item.STATE === 'ENABLE' ? '활성' : '비활성'"
                    color="green"
                    dense inset hide-details
                    class="ma-0 pa-0 token-state-switch"
                    @change="toggleTemplateState(item)"
                  ></v-switch>
                </template>
                <template v-slot:[`item.REWARD_COUNT`]="{ item }">
                  <span :class="{ 'red--text': (item.REWARD_COUNT || 0) === 0 }">{{ item.REWARD_COUNT || 0 }}</span>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn small outlined color="primary" class="mr-1" @click="openTemplateDialog(item)"><v-icon left small>mdi-pencil</v-icon>편집</v-btn>
                  <v-btn small outlined color="teal" @click="goRewardTab(item)"><v-icon left small>mdi-gift-outline</v-icon>보상</v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- ============ 2) 보상 관리 ============ -->
        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <div class="d-flex align-center mb-3">
                <v-select
                  v-model="rewardTemplatePid"
                  :items="templateOptions"
                  item-text="label"
                  item-value="pid"
                  label="템플릿 선택"
                  dense outlined hide-details style="max-width:360px"
                  @change="loadRewards"
                ></v-select>
                <v-spacer></v-spacer>
                <v-btn small color="teal" dark depressed :disabled="!rewardTemplatePid" @click="openRewardDialog()">
                  <v-icon left small>mdi-plus</v-icon>새 보상
                </v-btn>
                <v-btn small text class="ml-1" :disabled="!rewardTemplatePid" @click="loadRewards"><v-icon small>mdi-refresh</v-icon></v-btn>
              </div>

              <div v-if="selectedRewardTemplate && selectedRewardTemplate.ENTRY_TYPE === 'SINGLE'" class="mb-3">
                <v-alert dense text type="info" class="mb-1">
                  SINGLE 템플릿 — 후보 보상 중 <b>이번에 걸 보상</b>을 지정하세요. (현재 지정:
                  <b>{{ chosenRewardLabel }}</b>)
                </v-alert>
                <div class="d-flex align-center">
                  <v-select
                    v-model="chosenRewardPid"
                    :items="rewards.map(r => ({ label: `#${r.PID} · ${r.TITLE || '(제목없음)'}`, pid: r.PID }))"
                    item-text="label" item-value="pid"
                    label="지정 보상 선택" dense outlined hide-details style="max-width:360px"
                  ></v-select>
                  <v-btn small color="purple" dark depressed class="ml-2" :disabled="!chosenRewardPid" @click="setChosenReward">
                    <v-icon left small>mdi-star</v-icon>지정
                  </v-btn>
                </div>
              </div>

              <v-data-table
                :headers="rewardHeaders"
                :items="rewards"
                :loading="loadingRewards"
                dense
                :items-per-page="20"
                class="elevation-1"
                no-data-text="템플릿을 선택하고 보상을 등록하세요."
              >
                <template v-slot:[`item.chosen`]="{ item }">
                  <v-icon v-if="item.PID === chosenPid" color="purple" title="현재 지정된 보상">mdi-check-circle</v-icon>
                  <span v-else class="grey--text">-</span>
                </template>
                <template v-slot:[`item.THUMB_IMG_URL`]="{ item }">
                  <v-avatar tile size="36" v-if="item.THUMB_IMG_URL"><img :src="imgUrl(item.THUMB_IMG_URL)"/></v-avatar>
                  <v-icon v-else small color="grey lighten-1">mdi-image-off-outline</v-icon>
                </template>
                <template v-slot:[`item.REWARD_TYPE`]="{ item }">
                  <v-chip x-small :color="item.REWARD_TYPE === 'COUPON' ? 'orange lighten-4' : 'light-green lighten-4'">{{ item.REWARD_TYPE }}</v-chip>
                </template>
                <template v-slot:[`item.detail`]="{ item }">
                  <span v-if="item.REWARD_TYPE === 'MINER'">채굴기 {{ item.MINER_AMOUNT }}개 · {{ item.MINER_DURATION_SEC }}초</span>
                  <span v-else>상품코드: {{ item.PRODUCT_CODE || '-' }}</span>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn icon small color="primary" @click="openRewardDialog(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
                  <v-btn icon small color="red" @click="deleteReward(item)"><v-icon small>mdi-delete</v-icon></v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- ============ 3) 회차 · 모니터링 ============ -->
        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <div class="d-flex align-center mb-3 flex-wrap">
                <v-select
                  v-model="roundFilterTemplate"
                  :items="[{ label: '전체 템플릿', pid: 0 }].concat(templateOptions)"
                  item-text="label" item-value="pid"
                  label="템플릿" dense outlined hide-details style="max-width:280px" class="mr-2 mb-1"
                ></v-select>
                <v-select
                  v-model="roundFilterState"
                  :items="[{t:'전체',v:''},{t:'PLAYING',v:'PLAYING'},{t:'DRAWN',v:'DRAWN'},{t:'CLOSED',v:'CLOSED'}]"
                  item-text="t" item-value="v"
                  label="상태" dense outlined hide-details style="max-width:180px" class="mr-2 mb-1"
                ></v-select>
                <v-btn small color="deep-orange" dark depressed class="mb-1" @click="loadRounds"><v-icon left small>mdi-magnify</v-icon>조회</v-btn>
              </div>

              <v-data-table
                :headers="roundHeaders"
                :items="rounds"
                :loading="loadingRounds"
                dense
                :items-per-page="20"
                class="elevation-1"
                no-data-text="조회 결과가 없습니다."
              >
                <template v-slot:[`item.STATE`]="{ item }">
                  <v-chip x-small :color="roundStateColor(item.STATE)">{{ item.STATE }}</v-chip>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn icon small color="primary" @click="openRoundDetail(item)" title="상세/응모자"><v-icon small>mdi-eye</v-icon></v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- ===== 템플릿 등록/수정 다이얼로그 ===== -->
    <v-dialog v-model="templateDialog" max-width="720" :fullscreen="$vuetify.breakpoint.xsOnly" persistent scrollable>
      <v-card>
        <v-card-title>{{ editingTemplate.PID ? '템플릿 수정' : '템플릿 등록' }} <span v-if="editingTemplate.PID" class="text-caption grey--text ml-2">#{{ editingTemplate.PID }}</span></v-card-title>
        <v-divider/>
        <v-card-text style="max-height:70vh">
          <v-row dense class="mt-1">
            <v-col cols="12">
              <div class="text-caption mb-1">배너 이미지 <span class="grey--text">· 권장 크기 400 × 200 px</span></div>
              <div class="d-flex align-center">
                <v-avatar tile size="72" class="mr-3" style="border:1px solid #ddd" v-if="editingTemplate.BANNER_IMG_URL">
                  <img :src="imgUrl(editingTemplate.BANNER_IMG_URL)"/>
                </v-avatar>
                <v-icon v-else size="56" color="grey lighten-1" class="mr-3">mdi-image-off</v-icon>
                <v-file-input accept="image/*" label="배너 업로드" dense outlined hide-details prepend-icon="mdi-image"
                              :loading="bannerUploading" @change="onBannerChange" class="flex-grow-1"></v-file-input>
              </div>
            </v-col>
            <v-col cols="12"><v-text-field v-model="editingTemplate.TITLE" label="게임명 *" dense outlined hide-details></v-text-field></v-col>
            <v-col cols="12"><v-textarea v-model="editingTemplate.DESCRIPTION" label="설명" dense outlined hide-details rows="2" auto-grow></v-textarea></v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.number="editingTemplate.ENTRY_COST" type="number" min="0" label="참여비용(토큰/1회)" hint="1회 응모에 소모되는 토큰" persistent-hint dense outlined></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="editingTemplate.ENTRY_TYPE" :items="[{text:'RANK(등수 리스트)',value:'RANK'},{text:'SINGLE(단일 1등)',value:'SINGLE'}]" label="참여타입" hint="RANK=등수별 다수 당첨 / SINGLE=후보 중 1등 1명" persistent-hint dense outlined></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.number="editingTemplate.SORT_ORDER" type="number" label="노출 순서" hint="목록 정렬(작을수록 위)" persistent-hint dense outlined></v-text-field>
            </v-col>
          </v-row>
          <v-row dense class="mt-1">
            <v-col cols="12" md="4">
              <v-select
                v-model="editingTemplate.STATE"
                :items="[{text:'활성', value:'ENABLE'},{text:'비활성', value:'DISABLE'}]"
                label="활성화 여부"
                dense outlined hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" class="d-flex align-center pt-2">
              <div class="text-caption grey--text">
                '활성'으로 저장하려면 보상이 최소 1건 등록돼 있어야 합니다(없으면 자동으로 비활성 저장). 활성 템플릿만 게임서버 크론이 회차를 생성합니다.
                <span v-if="!editingTemplate.PID"> · 신규는 저장 후 보상을 먼저 등록해야 활성 저장이 됩니다.</span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="templateDialog = false">취소</v-btn>
          <v-btn color="deep-orange" dark depressed :loading="savingTemplate" @click="saveTemplate">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== 보상 등록/수정 다이얼로그 ===== -->
    <v-dialog v-model="rewardDialog" max-width="720" :fullscreen="$vuetify.breakpoint.xsOnly" persistent scrollable>
      <v-card>
        <v-card-title>{{ editingReward.PID ? '보상 수정' : '보상 등록' }}</v-card-title>
        <v-divider/>
        <v-card-text style="max-height:70vh">
          <v-row dense class="mt-1">
            <v-col cols="6" md="3"><v-text-field v-model.number="editingReward.RANK_NO" type="number" min="1" label="등수(RANK_NO)" hint="1등,2등… 정의 안 된 등수=꽝" persistent-hint dense outlined></v-text-field></v-col>
            <v-col cols="6" md="3"><v-text-field v-model.number="editingReward.WINNER_COUNT" type="number" min="1" label="당첨자 수" hint="이 등수 당첨 인원" persistent-hint dense outlined></v-text-field></v-col>
            <v-col cols="12" md="3"><v-select v-model="editingReward.REWARD_TYPE" :items="[{text:'채굴기(MINER)',value:'MINER'},{text:'쿠폰(COUPON)',value:'COUPON'}]" label="보상타입" hint="MINER=채굴기 / COUPON=다우상품" persistent-hint dense outlined></v-select></v-col>
            <v-col cols="12" md="3"><v-text-field v-model.number="editingReward.SORT_ORDER" type="number" label="정렬(표시순서)" hint="작을수록 위 · 추첨/당첨과 무관" persistent-hint dense outlined></v-text-field></v-col>

            <!-- MINER 분기 -->
            <template v-if="editingReward.REWARD_TYPE === 'MINER'">
              <v-col cols="6"><v-text-field v-model.number="editingReward.MINER_AMOUNT" type="number" min="1" label="채굴기 수량 *" dense outlined hide-details></v-text-field></v-col>
              <v-col cols="6">
                <v-text-field v-model.number="editingReward.MINER_DURATION_SEC" type="number" min="1" label="지속(초) *" dense outlined hide-details></v-text-field>
                <div class="mt-2 d-flex flex-wrap" style="gap:6px;">
                  <v-btn
                    v-for="preset in minerDurationPresets"
                    :key="preset.sec"
                    x-small
                    :color="Number(editingReward.MINER_DURATION_SEC) === preset.sec ? 'primary' : ''"
                    :outlined="Number(editingReward.MINER_DURATION_SEC) !== preset.sec"
                    @click="editingReward.MINER_DURATION_SEC = preset.sec"
                  >
                    <v-icon v-if="Number(editingReward.MINER_DURATION_SEC) === preset.sec" left x-small>mdi-check</v-icon>
                    {{ preset.label }}
                  </v-btn>
                </div>
              </v-col>
            </template>
            <!-- COUPON 분기 -->
            <template v-else>
              <v-col cols="12">
                <v-autocomplete
                  v-model="editingReward.PRODUCT_CODE"
                  :items="daouProducts"
                  :loading="loadingProducts"
                  item-text="__label" item-value="PRODUCT_CODE"
                  label="다우 상품 선택 *"
                  placeholder="상품명 / 브랜드 / 코드로 검색"
                  dense outlined clearable
                  hint="선택하면 상품코드(NO_REQ)와 썸네일이 자동 입력됩니다. (소스: db_ops.COUPON_PRODUCT · 노출ON·판매가능)"
                  persistent-hint
                  @change="onProductSelect"
                >
                  <template v-slot:item="{ item }">
                    <v-avatar tile size="30" class="mr-2" v-if="item.GOODS_IMAGE"><img :src="item.GOODS_IMAGE"/></v-avatar>
                    <v-icon v-else small class="mr-2" color="grey lighten-1">mdi-image-off-outline</v-icon>
                    <span class="text-body-2">{{ item.__label }}</span>
                  </template>
                </v-autocomplete>
                <div class="text-caption grey--text mt-1">선택된 상품코드(PRODUCT_CODE): <b>{{ editingReward.PRODUCT_CODE || '-' }}</b></div>
              </v-col>
              <v-col cols="12">
                <div class="text-caption mb-1">썸네일 <span class="grey--text">(상품 선택 시 자동 채움 · 필요 시 직접 업로드로 덮어쓰기)</span></div>
                <div class="d-flex align-center">
                  <v-avatar tile size="64" class="mr-3" style="border:1px solid #ddd" v-if="editingReward.THUMB_IMG_URL"><img :src="imgUrl(editingReward.THUMB_IMG_URL)"/></v-avatar>
                  <v-icon v-else size="48" color="grey lighten-1" class="mr-3">mdi-image-off</v-icon>
                  <v-file-input accept="image/*" label="썸네일 직접 업로드(선택)" dense outlined hide-details prepend-icon="mdi-image"
                                :loading="thumbUploading" @change="onThumbChange" class="flex-grow-1"></v-file-input>
                </div>
              </v-col>
            </template>

            <v-col cols="12"><v-text-field v-model="editingReward.TITLE" label="표시용 타이틀" dense outlined hide-details></v-text-field></v-col>
            <v-col cols="12"><v-textarea v-model="editingReward.DESCRIPTION" label="표시용 설명" dense outlined hide-details rows="2" auto-grow></v-textarea></v-col>
          </v-row>
          <div class="text-caption grey--text mt-2">RANK 타입: 등수별 보상 행 등록(정의 안 된 등수=꽝). SINGLE 타입: 후보 여러 개 등록 후 목록에서 "지정".</div>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="rewardDialog = false">취소</v-btn>
          <v-btn color="teal" dark depressed :loading="savingReward" @click="saveReward">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== 회차 상세 다이얼로그 ===== -->
    <v-dialog v-model="roundDialog" max-width="960" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable>
      <v-card v-if="roundDetail">
        <v-toolbar dark dense color="#37474F" flat>
          <v-toolbar-title>회차 #{{ roundDetail.PID }} · {{ roundDetail.TEMPLATE_TITLE }} ({{ roundDetail.ROUND_NO }}회차)</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-chip small :color="roundStateColor(roundDetail.STATE)" class="mr-2" text-color="black">{{ roundDetail.STATE }}</v-chip>
          <v-btn icon @click="roundDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text style="max-height:72vh">
          <v-row dense class="mt-2 text-body-2">
            <v-col cols="6" md="3">응모자수: <b>{{ roundDetail.ENTRY_COUNT }}</b></v-col>
            <v-col cols="6" md="3">총 기여도: <b>{{ roundDetail.ENTRY_TOKEN_TOTAL }}</b></v-col>
            <v-col cols="6" md="3">1등: <b>{{ roundDetail.WIN_USER_ID || '-' }}</b></v-col>
            <v-col cols="6" md="3">시작: {{ fmt(roundDetail.DT_START) }}</v-col>
          </v-row>

          <div class="d-flex align-center mt-4 mb-2">
            <div class="text-subtitle-2 font-weight-bold">응모 · 당첨</div>
            <v-spacer></v-spacer>
            <v-select v-model="bidFilterDispatch" :items="[{t:'전체',v:''},{t:'WAIT',v:'WAIT'},{t:'DONE',v:'DONE'},{t:'FAIL',v:'FAIL'}]"
                      item-text="t" item-value="v" label="지급상태" dense outlined hide-details style="max-width:150px" class="mr-2" @change="loadBids"></v-select>
            <v-btn small text @click="loadBids"><v-icon small>mdi-refresh</v-icon></v-btn>
          </div>

          <v-data-table :headers="bidHeaders" :items="bids" :loading="loadingBids" dense :items-per-page="30" class="elevation-1">
            <template v-slot:[`item.RESULT`]="{ item }">
              <v-chip x-small :color="item.RESULT === 'WIN' ? 'green lighten-4' : (item.RESULT === 'LOSE' ? 'grey lighten-2' : 'blue lighten-5')">{{ item.RESULT }}</v-chip>
            </template>
            <template v-slot:[`item.DISPATCH_STATE`]="{ item }">
              <v-chip v-if="item.DISPATCH_STATE" x-small :color="dispatchColor(item.DISPATCH_STATE)">{{ item.DISPATCH_STATE }}</v-chip>
              <span v-else>-</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn v-if="item.RESULT === 'WIN' && item.DISPATCH_STATE === 'FAIL'" x-small color="orange" dark depressed @click="retryDispatch(item)">
                <v-icon left x-small>mdi-refresh</v-icon>재처리
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { cpu_auction } from '../../api';
import config from '../../config/config';
const { FileUpload_Async } = require('../../util/fileutil');

export default {
  name: 'CPU_TokenContent',
  data() {
    return {
      tab: 0,
      // 템플릿
      templates: [],
      loadingTemplates: false,
      templateDialog: false,
      savingTemplate: false,
      bannerUploading: false,
      editingTemplate: this.emptyTemplate(),
      templateHeaders: [
        { text: '배너', value: 'BANNER_IMG_URL', sortable: false, width: 60 },
        { text: '게임명', value: 'TITLE' },
        { text: '타입', value: 'ENTRY_TYPE', width: 90 },
        { text: '참여비용', value: 'ENTRY_COST', width: 90 },
        { text: '보상수', value: 'REWARD_COUNT', width: 70 },
        { text: '정렬', value: 'SORT_ORDER', width: 60 },
        { text: '상태', value: 'STATE', width: 80 },
        { text: '관리', value: 'actions', sortable: false, width: 140 },
      ],
      // 보상
      rewardTemplatePid: null,
      rewards: [],
      loadingRewards: false,
      rewardDialog: false,
      savingReward: false,
      thumbUploading: false,
      editingReward: this.emptyReward(),
      // 채굴기 보상 지속(초) 프리셋 버튼 (현재값과 같으면 버튼 강조)
      minerDurationPresets: [
        { label: '1일', sec: 86400 },
        { label: '7일', sec: 604800 },
        { label: '14일', sec: 1209600 },
        { label: '30일', sec: 2592000 },
      ],
      chosenRewardPid: null,
      daouProducts: [],
      loadingProducts: false,
      rewardHeaders: [
        { text: '지정', value: 'chosen', sortable: false, width: 48, align: 'center' },
        { text: '썸네일', value: 'THUMB_IMG_URL', sortable: false, width: 60 },
        { text: '등수', value: 'RANK_NO', width: 60 },
        { text: '타이틀', value: 'TITLE' },
        { text: '타입', value: 'REWARD_TYPE', width: 90 },
        { text: '당첨자수', value: 'WINNER_COUNT', width: 80 },
        { text: '상세', value: 'detail', sortable: false },
        { text: '관리', value: 'actions', sortable: false, width: 100 },
      ],
      // 모니터링
      roundFilterTemplate: 0,
      roundFilterState: '',
      rounds: [],
      loadingRounds: false,
      roundDialog: false,
      roundDetail: null,
      roundHeaders: [
        { text: '회차PID', value: 'PID', width: 80 },
        { text: '템플릿', value: 'TEMPLATE_TITLE' },
        { text: '회차', value: 'ROUND_NO', width: 60 },
        { text: '상태', value: 'STATE', width: 90 },
        { text: '응모자', value: 'ENTRY_COUNT', width: 70 },
        { text: '총기여도', value: 'ENTRY_TOKEN_TOTAL', width: 90 },
        { text: '1등', value: 'WIN_USER_ID', width: 100 },
        { text: '시작', value: 'DT_START' },
        { text: '', value: 'actions', sortable: false, width: 50 },
      ],
      // bid
      bids: [],
      loadingBids: false,
      bidFilterDispatch: '',
      bidHeaders: [
        { text: '유저', value: 'USER_ID' },
        { text: '기여도', value: 'CONTRIB_TOKEN', width: 90 },
        { text: '응모수', value: 'BID_COUNT', width: 70 },
        { text: '결과', value: 'RESULT', width: 80 },
        { text: '등수', value: 'RANK_NO', width: 60 },
        { text: '지급상태', value: 'DISPATCH_STATE', width: 90 },
        { text: '', value: 'actions', sortable: false, width: 90 },
      ],
    };
  },
  computed: {
    templateOptions() {
      return this.templates.map(t => ({ label: `#${t.PID} · ${t.TITLE || '(제목없음)'} [${t.ENTRY_TYPE}]`, pid: t.PID }));
    },
    selectedRewardTemplate() {
      return this.templates.find(t => t.PID === this.rewardTemplatePid) || null;
    },
    chosenRewardLabel() {
      const t = this.selectedRewardTemplate;
      if (!t || !t.CHOSEN_REWARD_PID) return '미지정';
      const r = this.rewards.find(x => x.PID === t.CHOSEN_REWARD_PID);
      return r ? `#${r.PID} ${r.TITLE || ''}` : `#${t.CHOSEN_REWARD_PID}`;
    },
    // 현재 지정된 보상 PID (SINGLE 템플릿) — 보상 리스트에서 체크 표시용
    chosenPid() {
      const t = this.selectedRewardTemplate;
      return (t && t.CHOSEN_REWARD_PID) ? t.CHOSEN_REWARD_PID : 0;
    },
  },
  created() {
    this.loadTemplates();
  },
  methods: {
    emptyTemplate() {
      return { PID: 0, TITLE: '', BANNER_IMG_URL: '', DESCRIPTION: '', ENTRY_COST: 0, ENTRY_TYPE: 'RANK', SORT_ORDER: 0, STATE: 'DISABLE' };
    },
    emptyReward() {
      return { PID: 0, TEMPLATE_PID: 0, RANK_NO: 1, WINNER_COUNT: 1, REWARD_TYPE: 'MINER',
        MINER_AMOUNT: 1, MINER_DURATION_SEC: 3600, PRODUCT_CODE: '', TITLE: '', DESCRIPTION: '', THUMB_IMG_URL: '', SORT_ORDER: 0 };
    },
    imgUrl(key) {
      if (!key) return '';
      if (/^https?:\/\//.test(key)) return key;
      return config.s3_ImgQuizBucket + key;
    },
    fmt(v) { return v ? String(v).replace('T', ' ').substring(0, 19) : '-'; },
    templateRowClass(item) { return (item && item.STATE !== 'ENABLE') ? 'tpl-row-disabled' : ''; },
    roundStateColor(s) { return s === 'PLAYING' ? 'blue lighten-4' : (s === 'DRAWN' ? 'amber lighten-3' : 'grey lighten-2'); },
    dispatchColor(s) { return s === 'DONE' ? 'green lighten-4' : (s === 'FAIL' ? 'red lighten-4' : 'blue lighten-5'); },
    async uploadImage(file, subdir) {
      const m = (file.name || '').match(/\.[^.]+$/);
      const ext = m ? m[0] : '.png';
      const key = `project_cpu/auction/${subdir}/auc_${Date.now()}_${Math.floor(Math.random() * 100000)}${ext}`;
      const ok = await FileUpload_Async(file, key);
      return ok ? key : null;
    },

    // ===== 템플릿 =====
    async loadTemplates() {
      this.loadingTemplates = true;
      try {
        const res = await cpu_auction.template_list(false);
        this.templates = (res && res.list) || [];
      } catch (e) { alert('템플릿 조회 실패: ' + e); }
      finally { this.loadingTemplates = false; }
    },
    openTemplateDialog(item) {
      this.editingTemplate = item ? {
        PID: item.PID, TITLE: item.TITLE || '', BANNER_IMG_URL: item.BANNER_IMG_URL || '',
        DESCRIPTION: item.DESCRIPTION || '', ENTRY_COST: item.ENTRY_COST || 0,
        ENTRY_TYPE: item.ENTRY_TYPE || 'RANK', SORT_ORDER: item.SORT_ORDER || 0,
        STATE: item.STATE || 'DISABLE',
      } : this.emptyTemplate();
      this.templateDialog = true;
    },
    async onBannerChange(file) {
      if (!file) return;
      this.bannerUploading = true;
      try {
        const key = await this.uploadImage(file, 'banner');
        if (key) this.$set(this.editingTemplate, 'BANNER_IMG_URL', key);
        else alert('배너 업로드 실패');
      } catch (e) { alert('배너 업로드 오류: ' + e); }
      finally { this.bannerUploading = false; }
    },
    async saveTemplate() {
      if (!this.editingTemplate.TITLE || !this.editingTemplate.TITLE.trim()) { alert('게임명을 입력하세요.'); return; }
      if (Number(this.editingTemplate.ENTRY_COST) < 0) { alert('참여비용은 0 이상이어야 합니다.'); return; }
      this.savingTemplate = true;
      try {
        const body = {
          pid: this.editingTemplate.PID || 0,
          title: this.editingTemplate.TITLE.trim(),
          banner_img_url: this.editingTemplate.BANNER_IMG_URL || '',
          description: this.editingTemplate.DESCRIPTION || '',
          entry_cost: Number(this.editingTemplate.ENTRY_COST) || 0,
          entry_type: this.editingTemplate.ENTRY_TYPE || 'RANK',
          sort_order: Number(this.editingTemplate.SORT_ORDER) || 0,
          state: this.editingTemplate.STATE === 'ENABLE' ? 'ENABLE' : 'DISABLE',
        };
        const res = await cpu_auction.template_save(body);
        this.templateDialog = false;
        await this.loadTemplates();
        if (res && res.downgraded) {
          alert('보상이 없어 "비활성"으로 저장되었습니다.\n보상을 1건 이상 등록한 뒤 다시 활성으로 저장하세요.');
        }
      } catch (e) { alert('템플릿 저장 실패: ' + e); }
      finally { this.savingTemplate = false; }
    },
    async toggleTemplateState(item) {
      const next = item.STATE === 'ENABLE' ? 'DISABLE' : 'ENABLE';
      try {
        await cpu_auction.template_state(item.PID, next);
        await this.loadTemplates();
      } catch (e) {
        alert(next === 'ENABLE' ? ('활성화 실패(보상이 최소 1건 필요할 수 있음): ' + e) : ('상태 변경 실패: ' + e));
        await this.loadTemplates();   // 실패 시 스위치 상태 원복
      }
    },
    goRewardTab(item) {
      this.rewardTemplatePid = item.PID;
      this.tab = 1;
      this.loadRewards();
    },

    // ===== 보상 =====
    async loadRewards() {
      if (!this.rewardTemplatePid) { this.rewards = []; return; }
      this.loadingRewards = true;
      try {
        const res = await cpu_auction.reward_list(this.rewardTemplatePid);
        this.rewards = (res && res.list) || [];
        const t = this.selectedRewardTemplate;
        this.chosenRewardPid = (t && t.CHOSEN_REWARD_PID) ? t.CHOSEN_REWARD_PID : null;
      } catch (e) { alert('보상 조회 실패: ' + e); }
      finally { this.loadingRewards = false; }
    },
    openRewardDialog(item) {
      if (!this.rewardTemplatePid) { alert('먼저 템플릿을 선택하세요.'); return; }
      this.editingReward = item ? {
        PID: item.PID, TEMPLATE_PID: this.rewardTemplatePid,
        RANK_NO: item.RANK_NO || 1, WINNER_COUNT: item.WINNER_COUNT || 1, REWARD_TYPE: item.REWARD_TYPE || 'MINER',
        MINER_AMOUNT: item.MINER_AMOUNT || 1, MINER_DURATION_SEC: item.MINER_DURATION_SEC || 3600,
        PRODUCT_CODE: item.PRODUCT_CODE || '', TITLE: item.TITLE || '', DESCRIPTION: item.DESCRIPTION || '',
        THUMB_IMG_URL: item.THUMB_IMG_URL || '', SORT_ORDER: item.SORT_ORDER || 0,
      } : Object.assign(this.emptyReward(), { TEMPLATE_PID: this.rewardTemplatePid });
      this.loadDaouProducts();
      this.rewardDialog = true;
    },
    // 다우 상품 목록(드롭다운) — 최초 1회 로드 후 캐시
    async loadDaouProducts() {
      if (this.daouProducts.length || this.loadingProducts) return;
      this.loadingProducts = true;
      try {
        const res = await cpu_auction.product_list('');
        this.daouProducts = ((res && res.list) || []).map(p => ({
          ...p,
          __label: `${p.BRAND_NAME ? '[' + p.BRAND_NAME + '] ' : ''}${p.PRODUCT_NAME || '(이름없음)'} · ${p.FACE_VALUE || 0}원 (${p.PRODUCT_CODE})`,
        }));
      } catch (e) { /* 실패해도 무시 — 필요 시 재시도 */ }
      finally { this.loadingProducts = false; }
    },
    // 상품 선택 → 상품코드 확정 + 썸네일/타이틀 자동 채움
    onProductSelect(code) {
      const p = this.daouProducts.find(x => x.PRODUCT_CODE === code);
      if (!p) return;
      if (p.GOODS_IMAGE) this.$set(this.editingReward, 'THUMB_IMG_URL', p.GOODS_IMAGE);
      if (!this.editingReward.TITLE) this.$set(this.editingReward, 'TITLE', p.PRODUCT_NAME || '');
    },
    async onThumbChange(file) {
      if (!file) return;
      this.thumbUploading = true;
      try {
        const key = await this.uploadImage(file, 'thumb');
        if (key) this.$set(this.editingReward, 'THUMB_IMG_URL', key);
        else alert('썸네일 업로드 실패');
      } catch (e) { alert('썸네일 업로드 오류: ' + e); }
      finally { this.thumbUploading = false; }
    },
    async saveReward() {
      const r = this.editingReward;
      if (Number(r.WINNER_COUNT) < 1) { alert('당첨자 수는 1 이상이어야 합니다.'); return; }
      if (r.REWARD_TYPE === 'MINER') {
        if (Number(r.MINER_AMOUNT) <= 0 || Number(r.MINER_DURATION_SEC) <= 0) { alert('MINER: 수량과 지속(초)을 입력하세요.'); return; }
      } else {
        if (!r.PRODUCT_CODE || !r.PRODUCT_CODE.trim()) { alert('COUPON: 상품코드를 입력하세요.'); return; }
      }
      this.savingReward = true;
      try {
        const body = {
          pid: r.PID || 0, template_pid: r.TEMPLATE_PID,
          rank_no: Number(r.RANK_NO) || 1, winner_count: Number(r.WINNER_COUNT) || 1, reward_type: r.REWARD_TYPE,
          miner_amount: Number(r.MINER_AMOUNT) || 0, miner_duration_sec: Number(r.MINER_DURATION_SEC) || 0,
          product_code: r.PRODUCT_CODE || '', title: r.TITLE || '', description: r.DESCRIPTION || '',
          thumb_img_url: r.THUMB_IMG_URL || '', sort_order: Number(r.SORT_ORDER) || 0,
        };
        await cpu_auction.reward_save(body);
        this.rewardDialog = false;
        await this.loadRewards();
        await this.loadTemplates();  // 보상 개수 갱신
      } catch (e) { alert('보상 저장 실패: ' + e); }
      finally { this.savingReward = false; }
    },
    async deleteReward(item) {
      if (!confirm(`보상 #${item.PID} 을(를) 삭제하시겠습니까?`)) return;
      try {
        await cpu_auction.reward_delete(item.PID);
        await this.loadRewards();
        await this.loadTemplates();
      } catch (e) { alert('보상 삭제 실패: ' + e); }
    },
    async setChosenReward() {
      if (!this.chosenRewardPid) return;
      try {
        await cpu_auction.template_set_chosen_reward(this.rewardTemplatePid, this.chosenRewardPid);
        await this.loadTemplates();
        alert('지정 보상이 설정되었습니다.');
      } catch (e) { alert('지정 실패: ' + e); }
    },

    // ===== 모니터링 =====
    async loadRounds() {
      this.loadingRounds = true;
      try {
        const body = { limit: 200 };
        if (this.roundFilterTemplate) body.template_pid = this.roundFilterTemplate;
        if (this.roundFilterState) body.state = this.roundFilterState;
        const res = await cpu_auction.round_list(body);
        this.rounds = (res && res.list) || [];
      } catch (e) { alert('회차 조회 실패: ' + e); }
      finally { this.loadingRounds = false; }
    },
    async openRoundDetail(item) {
      try {
        const res = await cpu_auction.round_detail(item.PID);
        this.roundDetail = (res && res.round) || null;
        this.bidFilterDispatch = '';
        this.roundDialog = true;
        await this.loadBids();
      } catch (e) { alert('회차 상세 실패: ' + e); }
    },
    async loadBids() {
      if (!this.roundDetail) return;
      this.loadingBids = true;
      try {
        const body = { game_pid: this.roundDetail.PID, limit: 500 };
        if (this.bidFilterDispatch) body.dispatch_state = this.bidFilterDispatch;
        const res = await cpu_auction.bid_list(body);
        this.bids = (res && res.list) || [];
      } catch (e) { alert('응모 조회 실패: ' + e); }
      finally { this.loadingBids = false; }
    },
    async retryDispatch(item) {
      if (!confirm(`유저 ${item.USER_ID} 지급을 재처리(WAIT 전환)하시겠습니까?\n실제 재발행은 게임서버 지급 크론이 수행합니다.`)) return;
      try {
        const res = await cpu_auction.dispatch_retry({ bid_pid: item.PID });
        alert(`${(res && res.affected) || 0}건 재처리 대기로 전환됨`);
        await this.loadBids();
      } catch (e) { alert('재처리 실패: ' + e); }
    },
  },
};
</script>

<style scoped>
/* 비활성(DISABLE) 템플릿 행 — 회색 처리로 구분 */
.v-data-table >>> tr.tpl-row-disabled {
  background-color: #eeeeee !important;
}
.v-data-table >>> tr.tpl-row-disabled:hover {
  background-color: #e0e0e0 !important;
}
.v-data-table >>> tr.tpl-row-disabled td {
  color: #9e9e9e !important;
}
/* STATE 칼럼 스위치 — 라벨 작게, 행 안에 컴팩트하게 */
.token-state-switch {
  display: inline-flex;
}
.token-state-switch >>> .v-label {
  font-size: 12px;
}
</style>
